/**
 * Donnees associees (AAD) des enregistrements chiffres -- format v2.
 *
 * Les metadonnees claires (`type`, `folderId`, `favorite`, `revision`,
 * horodatages, `deletedAt`...) sont visibles du serveur, qui ne doit pas pouvoir
 * les modifier sans detection (faux tombstone, deplacement de dossier, revision
 * gonflee pour imposer une ancienne version : etude ETH Zurich / USENIX
 * Security 2026). Elles sont donc liees a l'AEAD en donnees associees : toute
 * alteration fait echouer le dechiffrement (`DecryptionFailedError`).
 *
 * Encodage canonique : tableau JSON a ordre fixe, valeurs strictement typees
 * (chaines, entiers surs, booleens, `null`) -- `JSON.stringify` est alors
 * deterministe. La validation stricte est indispensable : sans elle `NaN` ou
 * `undefined` seraient serialises en `null` et un `deletedAt: NaN` forge
 * s'ouvrirait sous l'AAD d'une entree active.
 *
 * Format v1 (historique) : AAD = `id` seul. Un `id` ne commence jamais par `[`,
 * les deux formats ne peuvent donc pas se confondre.
 */
import { DecryptionFailedError } from '@klavox/crypto-core';
import { InvalidVaultModelInputError } from './errors.js';

export const RECORD_AAD_VERSION = 2;

export type RecordKind = 'item' | 'folder' | 'attachment';

type AadValue = string | number | boolean | null;

export function recordAad(kind: RecordKind, fields: readonly AadValue[]): string {
  return JSON.stringify(['klavox.vault-record', RECORD_AAD_VERSION, kind, ...fields]);
}

export function assertId(value: unknown, name: string): asserts value is string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new InvalidVaultModelInputError(`${name}: chaine non vide attendue`);
  }
}

export function assertNullableId(value: unknown, name: string): asserts value is string | null {
  if (value !== null) assertId(value, name);
}

export function assertBoolean(value: unknown, name: string): asserts value is boolean {
  if (typeof value !== 'boolean') {
    throw new InvalidVaultModelInputError(`${name}: booleen attendu`);
  }
}

export function assertCount(value: unknown, name: string): asserts value is number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new InvalidVaultModelInputError(`${name}: entier positif attendu`);
  }
}

export function assertNullableCount(value: unknown, name: string): asserts value is number | null {
  if (value !== null) assertCount(value, name);
}

/**
 * Ouvre en format v2 ; si `acceptLegacyFormat`, retente en format v1 (AAD =
 * `id`) uniquement sur echec d'authentification. A reserver aux donnees
 * LOCALES a migrer : un enregistrement v1 recu d'un serveur reste falsifiable.
 */
export function openWithLegacyFallback<T>(acceptLegacyFormat: boolean | undefined, strict: () => T, legacy: () => T): T {
  try {
    return strict();
  } catch (cause) {
    if (acceptLegacyFormat !== true || !(cause instanceof DecryptionFailedError)) throw cause;
    return legacy();
  }
}
